import { Colors, Heading, Icon, InlineEditableText, Text } from '@amelio/ui-library';
import { useState } from 'react';

import type { MeetingTemplate, ThematicMeeting } from '@/types/thematic-meetings';
import type { ReactNode } from 'react';

import { useUpdateThematicMeetingTitle } from '@/api/thematic-meetings';
import meetingPlanArrow from '@/assets/illustrations/meetingPlanArrow.png';
import { BackLink } from '@/components/BackLink';
import { ARROW_GAP_PX } from '@/components/Illustrations/arrowPlacement';
import { PlanCard } from '@/components/MeetingPlan/PlanSection';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { MEETINGS_TITLE } from '@/pages/ThematicMeetings/components/meetingStyles';
import { MEETING_TITLE_MAX_LENGTH } from '@/pages/ThematicMeetings/constants';
import { AddToCalendarMenu } from '@/pages/ThematicMeetings/Plan/components/AddToCalendarMenu';
import { MeetingContextField } from '@/pages/ThematicMeetings/Plan/components/MeetingContextField';
import { MeetingDateField } from '@/pages/ThematicMeetings/Plan/components/MeetingDateField';
import { PlanIntroCard } from '@/pages/ThematicMeetings/Plan/components/PlanIntroCard';
import { SelectedEmployeeCard } from '@/pages/ThematicMeetings/Plan/components/SelectedEmployeeCard';
import { getTextByLanguage } from '@/utils/localization';

/** The hand-drawn arrow, in its PNG's own pixels: the canvas and the apex of the arrowhead, read off
 *  the opaque pixels. */
const ARROW_ARTWORK = { width: 372, height: 140, tip: { x: 364, y: 57 } } as const;

/** The drawing, turned around its tip: it lowers the tail so the curve comes out from under the intro
 *  card rather than beside it, and turns the head, drawn climbing, towards the employee card. */
const ARROW_TILT_DEG = 10;

/** The arrow hangs from the employee card: its tip is pinned against the middle of the card's left
 *  edge, and the tilt turns the drawing around that same tip, so the head lands on the card at every
 *  row width while the tail comes out under the intro card. */
const ARROW_PLACEMENT = {
    right: `calc(100% + ${ARROW_GAP_PX - (ARROW_ARTWORK.width - ARROW_ARTWORK.tip.x)}px)`,
    top: `calc(50% - ${ARROW_ARTWORK.tip.y}px)`,
    transformOrigin: `${ARROW_ARTWORK.tip.x}px ${ARROW_ARTWORK.tip.y}px`,
    transform: `rotate(${ARROW_TILT_DEG}deg)`,
};

interface MeetingPlanHeaderProps {
    meeting: ThematicMeeting;
    template: MeetingTemplate;
    onBack: () => void;
    dataTestId?: string;
}

export const MeetingPlanHeader = ({
    meeting,
    template,
    onBack,
    dataTestId = 'thematic-meeting-header',
}: MeetingPlanHeaderProps) => {
    const language = useSelectedAppLanguage();
    const { t } = useAmelioTranslations();
    const isCompleted = meeting.status === 'Completed';

    const title = meeting.title ?? getTextByLanguage(template.title, language);
    const objective = getTextByLanguage(template.objective, language);

    const { mutateAsync: updateTitle } = useUpdateThematicMeetingTitle();
    const [hasTitleSaveFailed, setHasTitleSaveFailed] = useState(false);
    // Only a failure is announced, under the title like the other fields of the plan: a title that
    // took is already on screen. The rejection lets the field put the previous title back.
    const saveTitle = async (nextTitle: string) => {
        setHasTitleSaveFailed(false);
        try {
            await updateTitle({ id: meeting.id, title: nextTitle });
        } catch (error) {
            setHasTitleSaveFailed(true);
            throw error;
        }
    };

    const renderTitle = (children: ReactNode) => (
        <Heading
            level={2}
            color={Colors.NAVY}
            className={MEETINGS_TITLE}
        >
            {children}
        </Heading>
    );

    return (
        <div className={'flex flex-col gap-6'}>
            <BackLink
                label={t('thematicMeetings.plan.backToMeetings')}
                onClick={onBack}
                dataTestId={'thematic-meeting-back-button'}
            />

            {/* Intro row: the "Create a Meeting Plan" card and a purely decorative
                restatement of the selected employee — neither carries any action, so nothing here
                needs the meeting or template data beyond the employee itself. `mb-6`: on top of the
                stack's own `gap-6`: the breathing room below this row is deliberately double the gap
                between the page's other blocks.

                Side by side from `lg` only, like the arrow between them: the employee column keeps
                its 365px card and the 240px left for the drawing, which leaves a tablet's intro card
                too narrow to read. */}
            <div
                className={
                    'mb-6 grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,400px)_1fr]'
                }
            >
                <PlanIntroCard />

                {/* Offsets drawn at a 16px root. `relative`: the box the arrow's tip is pinned
                    against. */}
                <div className={'relative lg:mt-[205px] lg:mr-[240px] lg:justify-self-end'}>
                    {/* Only from `lg`, where the two cards sit side by side: stacked, it would
                        point at nothing. `max-w-none`: the base `max-width: 100%` would squeeze
                        it to the card's width. */}
                    <img
                        src={meetingPlanArrow}
                        alt={''}
                        aria-hidden
                        width={ARROW_ARTWORK.width}
                        height={ARROW_ARTWORK.height}
                        className={'pointer-events-none absolute max-w-none max-lg:hidden'}
                        style={ARROW_PLACEMENT}
                        data-testid={'thematic-meeting-plan-arrow'}
                    />

                    <SelectedEmployeeCard employee={meeting.employee} />
                </div>
            </div>

            <PlanCard dataTestId={dataTestId}>
                {/* The title leads with the template's objective right under it, and the meeting's
                    date stays alone on the right: it is what a manager opening the plan looks for
                    first. The calendar follows the context, further down. `basis-64`: a line wraps
                    on what its items claim, and `flex-1` alone claims nothing, which would keep the
                    date beside a text column squeezed to a word per line. With it, the date drops
                    under the text as soon as both no longer fit side by side. */}
                <div className={'flex flex-wrap items-start justify-between gap-4'}>
                    <div className={'flex min-w-0 flex-1 basis-64 flex-col gap-2'}>
                        {isCompleted ? (
                            renderTitle(title)
                        ) : (
                            <InlineEditableText
                                value={title}
                                onSave={saveTitle}
                                editLabel={t('thematicMeetings.plan.editTitle')}
                                fieldLabel={t('thematicMeetings.plan.titleField')}
                                requiredMessage={t('thematicMeetings.plan.titleRequired')}
                                maxLength={MEETING_TITLE_MAX_LENGTH}
                                renderDisplay={renderTitle}
                                dataTestId={'thematic-meeting-title'}
                            />
                        )}

                        {hasTitleSaveFailed && (
                            <div role={'alert'}>
                                <Text
                                    size={'small'}
                                    weight={'medium'}
                                    color={Colors.ERROR_800}
                                >
                                    {t('thematicMeetings.plan.titleSaveError')}
                                </Text>
                            </div>
                        )}

                        {objective && (
                            <Text
                                color={Colors.BASE_500}
                                className={'max-w-3xl'}
                            >
                                {objective}
                            </Text>
                        )}
                    </div>

                    <MeetingDateField meeting={meeting} />
                </div>

                <MeetingContextField
                    meeting={meeting}
                    language={language}
                />

                {/* Under the context, on the left. Its own top margin carries the spacing, so an
                    absent context — the usual case on a meeting created without one — never lets
                    this button touch the text above it. `flex`: the menu's own root is inline-block,
                    so the row keeps it shrink-to-fit and off the text baseline. Offered with or
                    without a date: managers often prepare the meeting first and pick its slot in
                    their own calendar. */}
                {!isCompleted && (
                    <div className={'mt-6 flex'}>
                        <AddToCalendarMenu meeting={meeting} />
                    </div>
                )}

                {/* A completed meeting has no calendar row, so this follows the context directly:
                    why nothing in this header opens for editing. Same quiet treatment as the
                    schedule line below it. */}
                {isCompleted && (
                    <div className={'mt-3 flex items-center gap-2'}>
                        <Icon
                            name={'essential-info-circle'}
                            size={'sm'}
                            color={Colors.BASE_500}
                            ariaHidden
                        />
                        <Text
                            size={'small'}
                            color={Colors.BASE_500}
                        >
                            {t('thematicMeetings.plan.completedReadOnly')}
                        </Text>
                    </div>
                )}
            </PlanCard>
        </div>
    );
};
